/* eslint-disable @nx/enforce-module-boundaries */
import { Brifecase, Currency, Industry, SaveJobs,Arrowback, JobPlaceholderIcon } from '@mybridge/icons';
import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  IconButton,
  Image,
  Link,
  Spinner,
  Stack,
  Tag,
  Text,
} from '@mybridge/ui';
import { forwardRef, useContext, useEffect, useMemo, useRef } from 'react';
import { currencyFormatter, formatLocation } from 'v4/lib/commons';
import { EMPLOYMENT_CHOICES, WORKPLACE_TYPE } from 'v4/lib/data';
import { JobsListingContext } from '../context';
import moment from 'moment';
import { JobNotFoundIllustration } from '../404';
import { SavedJobIcon } from '@mybridge/icons/SavedJob';
import { ForceSignInWrapper } from 'v4/components/force-sign-in-wrapper';
import Head from 'next/head';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { getAllCompanyPages } from 'v4/store/actions/companyPages.actions';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { isMobile } from 'react-device-detect';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { formatJobDescription } from 'v4/utils/utils';
// eslint-disable-next-line react/display-name
export const JobDetails = forwardRef(({ ...props }, ref) => {
  const {
    jobDetails: job,
    jobDetailsFetching,
    appliedJobsListing,
    refetchAppliedJobsListing,
    refetchSavedJobsListing,
    setAppliedJob,
    applyDisc,
    reportDisc,
    setJobToReport,
    isSavedJob,
    saveJobIsLoading,
    saveJob,
    removeSavedJob,
    savedJobsListing,
    removeSavedJobIsLoading,
  } = useContext(JobsListingContext);

  const router = useRouter();
  const { t } = useTranslator();
  const direction = useDirection();
  const { push } = useRouter();
  const dispatch = useDispatch();
  const {companyPageList} = useSelector((state) => state.companyPage);
  const isPrivatePage = companyPageList?.some((page) => page?.id === job?.hiring_company_details?.id);

  
  useEffect(() => {
    refetchSavedJobsListing?.();
    refetchAppliedJobsListing?.();
    dispatch(getAllCompanyPages());
  }, []);

  // Handle hashtag link clicks for search navigation
  useEffect(() => {
    const handleHashtagClick = (e) => {
      const target = e.target.closest('.hashtag-link');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        const hashtag = target.getAttribute('data-hashtag');
        if (hashtag) {
          push(`/g/?query=${encodeURIComponent(hashtag)}`);
        }
      }
    };

    document.addEventListener('click', handleHashtagClick, true);
    
    return () => {
      document.removeEventListener('click', handleHashtagClick, true);
    };
  }, [push]);

  const hasTrackedJobDetails = useRef(false);

  // Add this function to get user slug
  const getUserSlug = () => {
    return userProfileInfo?.slug || userProfileInfo?.username || null;
  };

  // Add this function to track job details view
  const trackJobDetailsView = () => {
    const userSlug = getUserSlug();
    if (!userSlug || !job) return;

    trackEvent(ANALYTICS_EVENTS.JobDetail, userSlug, {
      page_path: router.asPath,
      page_title: job?.title || 'Job Details',
      job_id: job?.id,
      job_title: job?.title,
      company_id: job?.hiring_company_details?.id,
      company_name: job?.hiring_company_details?.name,
      employment_type: job?.employment_type,
      remote_type: job?.remote,
      salary_min: job?.compensation_range_min_amt,
      salary_max: job?.compensation_range_max_amt,
      currency: job?.currency_choice,
      is_applied: appliedJobsListing?.some(aj => aj?.job_details?.id === job?.id),
      is_saved: savedJobsListing?.some(sj => sj?.job_details?.id === job?.id),
      device_type: isMobile ? 'mobile' : 'desktop',
      timestamp: new Date().toISOString()
    });
  };

  useEffect(() => {
    if (job && !hasTrackedJobDetails.current) {
      trackJobDetailsView();
      hasTrackedJobDetails.current = true; // Mark as tracked
    }
  }, [job, appliedJobsListing, savedJobsListing]);

  const { loggedIn } = useSelector((state) => state.user);
  const { globalNotifCount } = useSelector((state) => state.notifications);
  const { userProfileInfo } = useSelector((state) => state.userProfile);

  const {
    hiring_company_details: company,
    currency_choice: currency,
    compensation_range_min_amt: minSalary,
    compensation_range_max_amt: maxSalary,
    benefits,
    employment_type: empType,
    job_description: description,
    skills,
    payment_choice,
    remote,
    is_draft,
    how_to_apply,
    how_to_apply_text,
  } = job ?? {};

  const applied = appliedJobsListing?.find(
    (aj) => aj?.job_details?.id === job?.id
  );

  if (jobDetailsFetching) {
    return (
      <HStack justifyContent="center">
        <Spinner />
      </HStack>
    );
  }

  if (!job || !job?.id) {
    return (
      <HStack justifyContent="center" py={12}>
        <Stack alignItems="center">
          <JobNotFoundIllustration />
          <Text>{t('applyJob.youHaveNotApplied') || "You have not applied to any job yet."}</Text>
        </Stack>
      </HStack>
    );
  }

  const handleApply = (job) => (e) => {
    setAppliedJob?.(job);
    applyDisc?.onOpen?.();
  };

  const handleReport = (job) => {
    setJobToReport?.(job);
    reportDisc?.onOpen?.();
  };

  const savedJob = savedJobsListing?.find?.(
    (sj) => sj?.job_details?.id === job?.id
  );
  
  const prevDefaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo_placeholder_image.png"
  const defaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo-placeholder-image.png";

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  return (
    <>
      <Head>
        <title>
          {loggedIn && globalNotifCount ? `(${globalNotifCount})` : ''}{' '}
          {job?.title ? job?.title : 'Jobs'} | myBridge.me
        </title>
      </Head>
      <Stack {...props} p={[2, '']}>
        {/* Back Button */}
        <Box w="100%" alignItems="center" justifyContent="flex-start">
          <Button
            variant="link"
            color="brandPrimary.500"
            onClick={() => router.back()}
            mb={4}
            fontWeight="600"
            gap="12px"
          >
            {direction === 'rtl' ? <ArrowForwardIcon /> : <Arrowback color="#3D5A80" width="12" height="12"/>} {t('buttons.back') || "Back"}
          </Button>
        </Box>
        <HStack alignItems="flex-start">
          <HStack flex={1}>
          {company?.image?.includes('logo_placeholder_image.png') || company?.image === prevDefaultCompanyImg || company?.image === null || company?.image === undefined ? (
            <Stack
              style={{ backgroundColor: '#D9D9D9' }}
              textAlign="center"
              alignItems="center"
              justifyContent="center"
              width="65px"
              height="65px"
              borderRadius="50%"
              overflow="hidden"
            >
              <JobPlaceholderIcon width="65%" height="65%" color="#9F9F9F" />
            </Stack>
          ) : (
            <Image
              border="1px solid"
              borderColor="blackAlpha.100"
              src={company?.image ?? defaultCompanyImg}
              w="65px"
              h="65px"
              borderRadius="50%"
              alt={company?.name}
            />
          )}
          </HStack>
          <HStack>
            <ForceSignInWrapper>
              <IconButton
                isLoading={saveJobIsLoading || removeSavedJobIsLoading}
                onClick={(e) => {
                  isSavedJob(job?.id)
                    ? removeSavedJob({ id: savedJob?.id })
                    : saveJob({ job_id: job?.id });
                }}
                variant="secondary"
              >
                {isSavedJob(job?.id) ? (
                  <SavedJobIcon width="20" height="20" />
                ) : (
                  <SaveJobs width="20" height="20" />
                )}
              </IconButton>
            </ForceSignInWrapper>
          </HStack>
        </HStack>
        <Stack spacing={0}>
          <Text color="brandPrimary.600" fontSize="md">
            {job?.title}
          </Text>
          <Box mb={4}>
            <Text 
              onClick={() => {
                isPrivatePage
                  ? push(`/page/private/${job?.hiring_company_details?.id}`)
                  : push(`/c/${job?.hiring_company_details?.slug}`);
              }}
              fontSize="sm"
              cursor="pointer"
            >{company?.name}</Text>
            {/* <Text display="inline"> | </Text> */}
            <Text fontSize="sm">{formatLocation(job)}</Text>
          </Box>
          {minSalary > 0 && (
            <HStack mb={1} alignItems="flex-start">
              <Box pt={'2px'}>
                <Currency />
              </Box>
              {maxSalary !== null || maxSalary > 0 ? (
                <Text fontSize="sm">
                  {currencyFormatter(minSalary, currency)} to{' '}
                  {currencyFormatter(maxSalary, currency)} {payment_choice}
                </Text>
              ) : (
                <Text fontSize="sm">
                  {currencyFormatter(minSalary, currency)} {payment_choice}
                </Text>
              )}
            </HStack>
          )}

          {benefits?.length > 0 && (
            <HStack mb={1} alignItems="flex-start">
              <Box pt={'2px'}>
                <Industry />
              </Box>
              <Text fontSize="sm">
                {benefits?.map?.((b) => b?.name)?.join(', ')}
              </Text>
            </HStack>
          )}

          <HStack alignItems="flex-start">
            <Box pt={'2px'}>
              <Brifecase />
            </Box>
            <Text fontSize="sm">
              {EMPLOYMENT_CHOICES?.find((ec) => ec.value === empType)?.label} |{' '}
              {WORKPLACE_TYPE?.find((ec) => ec.value === remote)?.label}
            </Text>
          </HStack>

          <Stack spacing={4} py={6}>
            {company?.why_work_here && (
              <Stack>
                <Text fontWeight="bold">{t('applyJob.whyWorkHere') || `Why work here`}</Text>
                <Text>{company?.why_work_here}</Text>
              </Stack>
            )}

            {skills?.length ? (
              <Stack>
                <Text fontWeight="bold">{t('applyJob.requiredSkills') || "Required Skills"}</Text>
                <HStack flexWrap="wrap">
                  {skills?.map((ben, benIndex) => (
                    <Tag py={2} px={3} borderRadius="full" key={benIndex}>
                      {ben?.name}
                    </Tag>
                  ))}
                </HStack>
              </Stack>
            ) : (
              ''
            )}
            {/* {applied &&
            applied?.candidate_details?.id === userProfileInfo?.id ? (
              <Text fontWeight="bold" color="green.500">
                You have already applied to this job!
                {t('jobs.applied') || "Applied"}
              </Text>
            ) : how_to_apply === 'EXTERNAL' ? (
              <Stack alignItems="flex-start">
                <Button
                  as={!is_draft ? Link : Button}
                  href={how_to_apply_text}
                  target="_blank"
                  variant="primary"
                  isDisabled={is_draft}
                >
                  {t('applyJob.applyNow') || "Apply Now"}
                </Button>
              </Stack>
            ) : (
              <ForceSignInWrapper>
                <Stack alignItems="flex-start">
                  <Button
                    onClick={handleApply(job)}
                    variant="primary"
                    isDisabled={is_draft}
                  >
                    {t('applyJob.applyNow') || "Apply Now"}
                  </Button>
                </Stack>
              </ForceSignInWrapper>
            )} */}
            {applied &&
            applied?.candidate_details?.id === userProfileInfo?.id ? (
              // <Text fontWeight="bold" color="green.500">
              //   {t('jobs.applied') || "Applied"}
              // </Text>
              <Button
                border="1px solid #C7C7C7"
                borderRadius="50px"
                bg="none"
                fontSize="14px"
                h="41px"
                color="green.500"
                fontWeight="500"
                alignSelf="flex-start" 
                px={4}
              >
                {t('jobs.applied') || "Applied"} | {formatDate(applied?.applied_date)}
              </Button>
            ) : how_to_apply === 'EXTERNAL' ? (
              <Stack alignItems="flex-start">
                <Button
                  onClick={() => {
                    // First, remove square brackets if present
                    let urlText = how_to_apply_text;
                    if (urlText.startsWith('[') && urlText.endsWith(']')) {
                      urlText = urlText.substring(1, urlText.length - 1);
                    }
                    
                    const cleanedUrl = urlText
                      .replace(/['"]/g, '')
                      .trim();
                    
                    const formattedUrl =
                      cleanedUrl.startsWith('https://') ||
                      cleanedUrl.startsWith('http://')
                        ? cleanedUrl
                        : `https://${cleanedUrl.replace(
                            /^https?:\/\//,
                            ''
                          )}`;

                    if (
                      cleanedUrl &&
                      cleanedUrl !== 'https://' &&
                      cleanedUrl !== 'http://'
                    ) {
                      try {
                        // Validate URL before opening
                        new URL(formattedUrl);
                        window?.open(formattedUrl, '_blank');
                      } catch (error) {
                        console.error('Invalid URL format:', formattedUrl, error);
                      }
                    } else {
                      console.error('Invalid URL:', formattedUrl);
                    }
                  }}
                  variant="primary"
                  borderRadius="full"
                  isDisabled={is_draft}
                >
                  {t('applyJob.applyNow') || "Apply Now"}
                </Button>
              </Stack>
            ) : (
              <ForceSignInWrapper>
                <Stack alignItems="flex-start">
                  <Button
                    borderRadius="30px"
                    onClick={handleApply(job)}
                    variant="primary"
                    isDisabled={is_draft}
                  >
                    {t('applyJob.applyNow') || "Apply Now"}
                  </Button>
                </Stack>
              </ForceSignInWrapper>
            )}

            {description?.length > 0 ? (
              <>
                <Box
                  dangerouslySetInnerHTML={{
                    __html: formatJobDescription(description) 
                  }}
                  // mt={2}
                  className="jobDetailsDescription"
                />
                {/* {applied &&
                applied?.candidate_details?.id === userProfileInfo?.id ? (
                  <Text fontWeight="bold" color="green.500">
                    You have already applied to this job!
                    {t('jobs.applied') || "Applied"}
                  </Text>
                ) : how_to_apply === 'EXTERNAL' ? (
                  <Stack alignItems="flex-start">
                    <Button
                      as={!is_draft ? Link : Button}
                      href={how_to_apply_text}
                      target="_blank"
                      variant="primary"
                      isDisabled={is_draft}
                    >
                      {t('applyJob.applyNow') || "Apply Now"}
                    </Button>
                  </Stack>
                ) : (
                  <ForceSignInWrapper>
                    <Stack alignItems="flex-start">
                      <Button
                        onClick={handleApply(job)}
                        variant="primary"
                        isDisabled={is_draft}
                      >
                        {t('applyJob.applyNow') || "Apply Now"}
                      </Button>
                    </Stack>
                  </ForceSignInWrapper>
                )} */}
                {applied &&
                applied?.candidate_details?.id === userProfileInfo?.id ? (
                  <Button
                    borderRadius="50px"
                    border="1px solid #C7C7C7"
                    fontSize="14px"
                    bg="none"
                    color="green.500"
                    h="41px"
                    alignSelf="flex-start" 
                    fontWeight="500"
                    px={4}
                    >
                    {t('jobs.applied') || "Applied"} | {formatDate(applied?.applied_date)}
                  </Button>
                ) : how_to_apply === 'EXTERNAL' ? (
                  <Stack alignItems="flex-start">
                    <Button
                      onClick={() => {
                        // First, remove square brackets if present
                        let urlText = how_to_apply_text;
                        if (urlText.startsWith('[') && urlText.endsWith(']')) {
                          urlText = urlText.substring(1, urlText.length - 1);
                        }
                        
                        const cleanedUrl = urlText
                          .replace(/['"]/g, '')
                          .trim();
                        
                        const formattedUrl =
                          cleanedUrl.startsWith('https://') ||
                          cleanedUrl.startsWith('http://')
                            ? cleanedUrl
                            : `https://${cleanedUrl.replace(
                                /^https?:\/\//,
                                ''
                              )}`;

                        if (
                          cleanedUrl &&
                          cleanedUrl !== 'https://' &&
                          cleanedUrl !== 'http://'
                        ) {
                          try {
                            // Validate URL before opening
                            new URL(formattedUrl);
                            window?.open(formattedUrl, '_blank');
                          } catch (error) {
                            console.error('Invalid URL format:', formattedUrl, error);
                          }
                        } else {
                          console.error('Invalid URL:', formattedUrl);
                        }
                      }}
                      variant="primary"
                      borderRadius="full"
                      isDisabled={is_draft}
                    >
                      {t('applyJob.applyNow') || "Apply Now"}
                    </Button>
                  </Stack>
                ) : (
                  <ForceSignInWrapper>
                    <Stack alignItems="flex-start">
                      <Button
                        borderRadius="30px"
                        onClick={handleApply(job)}
                        variant="primary"
                        isDisabled={is_draft}
                      >
                        {t('applyJob.applyNow') || "Apply Now"}
                      </Button>
                    </Stack>
                  </ForceSignInWrapper>
                )}
              </>
            ) : (
              ''
            )}
            <Box>
              <ForceSignInWrapper>
                <Link
                  onClick={(e) => handleReport(job)}
                  textDecoration="underline"
                  color="brandPrimary.500"
                >
                  {t('applyJob.reportJob') || "Report Job"}
                </Link>
              </ForceSignInWrapper>
            </Box>
            <Divider />
            <Stack>
              <HStack alignItems="baseline">
                <Text wordBreak="normal">{t('applyJob.companyAddress') || "Company Address"}:</Text>
                <Text>
                  {[company?.address, formatLocation(company)]
                    .filter?.((v) => v?.length > 0)
                    ?.join?.(', ')}
                </Text>
              </HStack>
              {job?.published_date?.length ? (
                <HStack>
                  <Text>{t('applyJob.posted') || "Posted"}:</Text>
                  <Text>
                    {[moment(job?.published_date)?.fromNow()]
                      .filter?.((v) => v?.length > 0)
                      ?.join?.(', ')}
                  </Text>
                </HStack>
              ) : (
                ''
              )}
              <Box>
                <Link
                  color="brandPrimary.500"
                  href={isPrivatePage ? `/page/private/${job?.hiring_company_details?.id}/jobs` : `/c/${job?.hiring_company_details?.slug}/jobs`}
                >
                  {t('applyJob.viewAllJobsAt') || "View all jobs at"} {company?.name}
                </Link>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
});
